import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import type {
  AdminMembersResponseDto,
  AdminOccurrenceDetailDto,
  MemberRole,
  StudiosResponseDto
} from "@madamuh/domain";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fetchAdminMembers, fetchAdminStudios } from "../../shared/api/adminDirectory";
import {
  applyAdminOccurrenceOverride,
  deleteAdminOccurrence,
  fetchAdminOccurrenceDetail,
  patchAdminOccurrence
} from "../../shared/api/admin-schedule";
import {
  formatTenantIsoDateTime,
  isoToTenantLocalDateTimeInput,
  tenantLocalDateTimeToIso
} from "../../shared/lib/tenantDateTime";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

function hasTeacherRole(roles: readonly MemberRole[]): boolean {
  return roles.includes("teacher") || roles.includes("admin");
}

function getMemberDisplayName(member: AdminMembersResponseDto["items"][number]): string {
  return member.appNickname ?? member.lineDisplayNameSnapshot ?? member.platformUserId;
}

function toggleSelection(current: readonly string[], value: string, checked: boolean): string[] {
  const next = new Set(current);

  if (checked) {
    next.add(value);
  } else {
    next.delete(value);
  }

  return [...next];
}

type LocationState = {
  feedbackMessage?: string;
} | null;

function buildDeleteConfirmationMessage(occurrence: AdminOccurrenceDetailDto): string {
  const lines = [
    "予定を削除しますか？",
    `${occurrence.lessonTitle} ${formatTenantIsoDateTime(occurrence.startAt)} - ${formatTenantIsoDateTime(occurrence.endAt)} をこのスケジュールから削除します。`
  ];

  if (occurrence.sourceType === "manual") {
    lines.push("個別追加した予定は Generate では再作成されません。");
  } else {
    lines.push("再度 Generate すると、定期枠が有効な場合は再作成されます。");
    lines.push("一時的に削除しただけの場合は、再度 Generate で復旧できます。");
    lines.push("今後も不要な場合は、レッスン詳細の定期枠を無効化してください。");
  }

  return lines.join("\n");
}

export function AdminOccurrenceDetailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenantSlug = "unknown-tenant", occurrenceId = "" } = useParams();
  const locationState = location.state as LocationState;
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [occurrence, setOccurrence] = useState<AdminOccurrenceDetailDto | null>(null);
  const [members, setMembers] = useState<AdminMembersResponseDto | null>(null);
  const [studios, setStudios] = useState<StudiosResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving">("idle");
  const [deleteState, setDeleteState] = useState<"idle" | "deleting">("idle");
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(locationState?.feedbackMessage ?? null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));

  const [editStartAt, setEditStartAt] = useState("");
  const [editEndAt, setEditEndAt] = useState("");
  const [studioId, setStudioId] = useState("");
  const [studioTbd, setStudioTbd] = useState(false);
  const [teacherPlatformUserIds, setTeacherPlatformUserIds] = useState<string[]>([]);
  const [teacherTbd, setTeacherTbd] = useState(false);
  const [publicNote, setPublicNote] = useState("");
  const [operatorNote, setOperatorNote] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [overrideType, setOverrideType] = useState("reschedule");
  const [causeType, setCauseType] = useState("clubDecision");
  const [newDate, setNewDate] = useState("");
  const [newStartAt, setNewStartAt] = useState("");
  const [newEndAt, setNewEndAt] = useState("");
  const [overridePublicNote, setOverridePublicNote] = useState("");
  const [overrideOperatorNote, setOverrideOperatorNote] = useState("");
  const [overrideAdminNote, setOverrideAdminNote] = useState("");
  const teacherMembers = useMemo(
    () => members?.items.filter((member) => hasTeacherRole(member.roles)) ?? [],
    [members]
  );

  function syncOccurrenceState(nextOccurrence: AdminOccurrenceDetailDto) {
    setOccurrence(nextOccurrence);
    setEditStartAt(isoToTenantLocalDateTimeInput(nextOccurrence.startAt));
    setEditEndAt(isoToTenantLocalDateTimeInput(nextOccurrence.endAt));
    setStudioId(nextOccurrence.studioId ?? "");
    setStudioTbd(nextOccurrence.studioDecisionStatus === "tbd" || !nextOccurrence.studioId);
    setTeacherPlatformUserIds([...nextOccurrence.teacherPlatformUserIds]);
    setTeacherTbd(nextOccurrence.teacherAssignmentStatus === "tbd" || nextOccurrence.teacherPlatformUserIds.length === 0);
    setPublicNote(nextOccurrence.publicNote ?? "");
    setOperatorNote(nextOccurrence.operatorNote ?? "");
    setAdminNote(nextOccurrence.adminNote ?? "");
    setOverridePublicNote(nextOccurrence.publicNote ?? "");
    setOverrideOperatorNote(nextOccurrence.operatorNote ?? "");
    setOverrideAdminNote(nextOccurrence.adminNote ?? "");
    setNewDate(nextOccurrence.occurrenceDate);
    setNewStartAt(isoToTenantLocalDateTimeInput(nextOccurrence.startAt));
    setNewEndAt(isoToTenantLocalDateTimeInput(nextOccurrence.endAt));
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole || !occurrenceId) {
      return;
    }

    let cancelled = false;

    async function loadOccurrence() {
      setLoadState("loading");
      setLoadError(null);
      setFeedbackMessage(null);

      try {
        const [nextOccurrence, nextMembers, nextStudios] = await Promise.all([
          fetchAdminOccurrenceDetail(tenantSlug, occurrenceId),
          fetchAdminMembers(tenantSlug),
          fetchAdminStudios(tenantSlug)
        ]);

        if (cancelled) {
          return;
        }

        syncOccurrenceState(nextOccurrence);
        setMembers(nextMembers);
        setStudios(nextStudios);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, "occurrence の取得に失敗しました"));
      }
    }

    void loadOccurrence();

    return () => {
      cancelled = true;
    };
  }, [hasAdminRole, isSessionReady, navigate, occurrenceId, tenantSlug]);

  async function handlePatch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!occurrence) {
      return;
    }

    setSaveState("saving");
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const nextStartAt = tenantLocalDateTimeToIso(editStartAt);
      const nextEndAt = tenantLocalDateTimeToIso(editEndAt);
      const nextTeacherIds = teacherTbd ? [] : teacherPlatformUserIds;
      const nextOccurrence = await patchAdminOccurrence(tenantSlug, occurrence.occurrenceId, {
        expectedRevision: occurrence.revision,
        startAt: nextStartAt ?? occurrence.startAt,
        endAt: nextEndAt ?? occurrence.endAt,
        studioId: studioTbd ? null : studioId.trim() || null,
        studioDecisionStatus: studioTbd ? "tbd" : "confirmed",
        teacherPlatformUserIds: nextTeacherIds,
        teacherAssignmentStatus: teacherTbd ? "tbd" : "assigned",
        publicNote: publicNote || null,
        operatorNote: operatorNote || null,
        adminNote: adminNote || null
      });

      syncOccurrenceState(nextOccurrence);
      setFeedbackMessage("この回だけの時間・会場・講師・メモを更新しました。");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "occurrence patch に失敗しました"));
    } finally {
      setSaveState("idle");
    }
  }

  async function handleOverride(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!occurrence) {
      return;
    }

    setSaveState("saving");
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const nextNewDate = newDate && newDate !== occurrence.occurrenceDate ? newDate : null;
      const nextNewStartAt = tenantLocalDateTimeToIso(newStartAt);
      const nextNewEndAt = tenantLocalDateTimeToIso(newEndAt);
      const nextOccurrence = await applyAdminOccurrenceOverride(tenantSlug, occurrence.occurrenceId, {
        expectedRevision: occurrence.revision,
        overrideType,
        causeType,
        newDate: nextNewDate,
        newStartAt: nextNewStartAt && nextNewStartAt !== occurrence.startAt ? nextNewStartAt : null,
        newEndAt: nextNewEndAt && nextNewEndAt !== occurrence.endAt ? nextNewEndAt : null,
        publicNote: overridePublicNote || null,
        operatorNote: overrideOperatorNote || null,
        adminNote: overrideAdminNote || null
      });

      syncOccurrenceState(nextOccurrence);
      setFeedbackMessage(
        overrideType === "cancel"
          ? "この回を休講にしました。"
          : "この回の振替・時間変更 override を保存しました。"
      );
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "override apply に失敗しました"));
    } finally {
      setSaveState("idle");
    }
  }

  async function handleDeleteOccurrence() {
    if (!occurrence) {
      return;
    }

    if (!window.confirm(buildDeleteConfirmationMessage(occurrence))) {
      return;
    }

    setDeleteState("deleting");
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const nextScheduleMonth = await deleteAdminOccurrence(tenantSlug, occurrence.occurrenceId, {
        expectedRevision: occurrence.revision
      });

      navigate(`/t/${tenantSlug}/teacher/admin/schedule-months/${nextScheduleMonth.yearMonth}`, {
        replace: true,
        state: {
          feedbackMessage: "予定を削除しました。"
        } satisfies LocationState
      });
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "予定の削除に失敗しました"));
    } finally {
      setDeleteState("idle");
    }
  }

  const adminLinks = [
    { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
    { to: `/t/:tenantSlug/teacher/admin/schedule-months/${occurrence?.yearMonth ?? "202604"}`, label: "月次スケジュール" }
  ];

  useEffect(() => {
    const nextAction = searchParams.get("action");

    if (nextAction === "cancel" || nextAction === "reschedule") {
      setOverrideType(nextAction);
    }
  }, [searchParams]);

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="Admin Occurrence Detail"
      description="occurrence の direct edit と override apply を試せる最小画面です。"
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>Entry へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は admin role のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>occurrence を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}

        {occurrence ? (
          <>
            <section style={{ display: "grid", gap: 8 }}>
              <strong style={{ fontSize: 20 }}>{occurrence.lessonTitle}</strong>
              <div style={{ fontSize: 14, opacity: 0.8 }}>occurrenceId: {occurrence.occurrenceId}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>revision: {occurrence.revision}</div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                {occurrence.sourceType} / {occurrence.syncState} / {occurrence.resolutionType}
              </div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                {formatTenantIsoDateTime(occurrence.startAt)} - {formatTenantIsoDateTime(occurrence.endAt)}
              </div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                studio: {occurrence.studioNameSnapshot ?? "未定"} / {occurrence.studioDecisionStatus ?? "unset"}
              </div>
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                teachers: {occurrence.teacherNameSnapshots.join(", ") || "未定"} / {occurrence.teacherAssignmentStatus ?? "unset"}
              </div>
              {!occurrence.publishedEver ? (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <button type="button" onClick={() => void handleDeleteOccurrence()} disabled={deleteState === "deleting"}>
                    {deleteState === "deleting" ? "削除中..." : "削除"}
                  </button>
                  <span style={{ fontSize: 12, opacity: 0.8 }}>
                    {occurrence.sourceType === "manual"
                      ? "個別追加した予定は、削除すると Generate では戻りません。"
                      : "一時的に削除しただけの場合は、再度 Generate で復旧できます。今後も不要な場合はレッスン詳細の定期枠を無効化してください。"}
                  </span>
                </div>
              ) : (
                <div style={{ fontSize: 12, opacity: 0.8 }}>
                  公開済みの予定は削除できません。休講または変更として処理してください。
                </div>
              )}
            </section>

            <form onSubmit={handlePatch} style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Direct Edit</h2>
              <label style={{ display: "grid", gap: 4 }}>
                <span>startAt</span>
                <input
                  type="datetime-local"
                  value={editStartAt}
                  onChange={(event) => setEditStartAt(event.target.value)}
                />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>endAt</span>
                <input type="datetime-local" value={editEndAt} onChange={(event) => setEditEndAt(event.target.value)} />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>studio</span>
                <select value={studioId} onChange={(event) => setStudioId(event.target.value)} disabled={studioTbd}>
                  <option value="">選択してください</option>
                  {studios?.items.map((studio) => (
                    <option key={studio.studioId} value={studio.studioId}>
                      {studio.displayName}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={studioTbd}
                  onChange={(event) => {
                    setStudioTbd(event.target.checked);

                    if (event.target.checked) {
                      setStudioId("");
                    }
                  }}
                />
                <span>会場未定</span>
              </label>
              <fieldset style={{ display: "grid", gap: 8, border: "none", padding: 0, margin: 0 }}>
                <legend style={{ padding: 0 }}>teachers</legend>
                <div style={{ display: "grid", gap: 8 }}>
                  {teacherMembers.map((member) => (
                    <label key={member.platformUserId} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={teacherPlatformUserIds.includes(member.platformUserId)}
                        disabled={teacherTbd}
                        onChange={(event) =>
                          setTeacherPlatformUserIds((current) =>
                            toggleSelection(current, member.platformUserId, event.target.checked)
                          )
                        }
                      />
                      <span>{getMemberDisplayName(member)}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={teacherTbd}
                  onChange={(event) => {
                    setTeacherTbd(event.target.checked);

                    if (event.target.checked) {
                      setTeacherPlatformUserIds([]);
                    }
                  }}
                />
                <span>講師未定</span>
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>publicNote</span>
                <textarea value={publicNote} onChange={(event) => setPublicNote(event.target.value)} rows={3} />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>operatorNote</span>
                <textarea value={operatorNote} onChange={(event) => setOperatorNote(event.target.value)} rows={3} />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>adminNote</span>
                <textarea value={adminNote} onChange={(event) => setAdminNote(event.target.value)} rows={3} />
              </label>
              <button type="submit" disabled={saveState === "saving"}>
                {saveState === "saving" ? "保存中..." : "この回だけ時間・会場・講師を更新する"}
              </button>
            </form>

            <form onSubmit={handleOverride} style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Semantic Override</h2>
              <label style={{ display: "grid", gap: 4 }}>
                <span>overrideType</span>
                <select value={overrideType} onChange={(event) => setOverrideType(event.target.value)}>
                  <option value="reschedule">reschedule</option>
                  <option value="cancel">cancel</option>
                </select>
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>causeType</span>
                <input value={causeType} onChange={(event) => setCauseType(event.target.value)} />
              </label>
              {overrideType === "reschedule" ? (
                <>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>newDate</span>
                    <input type="date" value={newDate} onChange={(event) => setNewDate(event.target.value)} />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>newStartAt</span>
                    <input
                      type="datetime-local"
                      value={newStartAt}
                      onChange={(event) => setNewStartAt(event.target.value)}
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>newEndAt</span>
                    <input type="datetime-local" value={newEndAt} onChange={(event) => setNewEndAt(event.target.value)} />
                  </label>
                </>
              ) : null}
              <label style={{ display: "grid", gap: 4 }}>
                <span>override publicNote</span>
                <textarea
                  value={overridePublicNote}
                  onChange={(event) => setOverridePublicNote(event.target.value)}
                  rows={3}
                />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>override operatorNote</span>
                <textarea
                  value={overrideOperatorNote}
                  onChange={(event) => setOverrideOperatorNote(event.target.value)}
                  rows={3}
                />
              </label>
              <label style={{ display: "grid", gap: 4 }}>
                <span>override adminNote</span>
                <textarea
                  value={overrideAdminNote}
                  onChange={(event) => setOverrideAdminNote(event.target.value)}
                  rows={3}
                />
              </label>
              <button type="submit" disabled={saveState === "saving"}>
                {saveState === "saving"
                  ? "保存中..."
                  : overrideType === "cancel"
                    ? "休講にする"
                    : "振替・時間変更 override を保存する"}
              </button>
              <div style={{ fontSize: 12, opacity: 0.8 }}>
                時間変更・会場変更・講師変更は Direct Edit を使い、休講・振替は Semantic Override を使います。
              </div>
            </form>

            <section style={{ display: "grid", gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Applied Overrides</h2>
              {occurrence.overrides.length === 0 ? <p style={{ margin: 0 }}>override はありません。</p> : null}
              {occurrence.overrides.map((override) => (
                <article
                  key={override.overrideId}
                  style={{ border: "1px solid #e5e5e5", borderRadius: 12, padding: 12, display: "grid", gap: 6 }}
                >
                  <strong>{override.overrideType}</strong>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>{override.causeType}</div>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>status: {override.status}</div>
                  {override.newStartAt ? <div style={{ fontSize: 14 }}>newStartAt: {override.newStartAt}</div> : null}
                  {override.newEndAt ? <div style={{ fontSize: 14 }}>newEndAt: {override.newEndAt}</div> : null}
                  {override.publicNote ? <div style={{ fontSize: 14 }}>publicNote: {override.publicNote}</div> : null}
                </article>
              ))}
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
